import React from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { RouteComponentProps } from 'react-router'

import T from 'components/FormattedMessage'
import Button from 'components/Button'
import TextInput from 'components/TextInput'
import ActionContext from 'components/ActionContext'
import CenteredBottom from 'components/CenteredBottom'
import CenteredContent from 'components/CenteredContent'
import NavigationHeader from 'components/NavigationHeader'
import UserPhotoWithUploadAndCropper from 'components/UserPhotoWithUploadAndCropper'

import { Grid, Typography, WithStyles, createStyles, withStyles, Theme, Snackbar } from '@material-ui/core'

import {
  NotificationSettingsResponse,
  UserInfoResponse,
  NotificationType,
  GeneralResponse,
  EditProfileResponse,
} from 'protos'
import { Action } from 'types'

import NotificationSwitchers from './components/NotificationSwitchers'

interface Props {
  getNotifSettings: () => Promise<NotificationSettingsResponse>
  changeUser: (
    nickname: string | undefined,
    picture: Blob | undefined,
    notificationSettings: { [k: string]: boolean },
  ) => Promise<[EditProfileResponse, GeneralResponse]>
  onUserInfoChange: (nickname: string, photoUrl: string) => void
  onLogoutClick: () => void
  userInfo: UserInfoResponse
}

type State = {
  isSomethingChanged: boolean
  nickname: string
  photoUrl: string
  notificationSettings: Action<NotificationSettingsResponse | undefined>
  errorCode?: string
}

const styles = (theme: Theme) => createStyles({
  logoutButtonContainer: {
    padding: theme.spacing(),
  },
  updateButtonContainer: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(),
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: theme.palette.grey['100'],
  },
  userName: {
    margin: `${theme.spacing() * 4}px 0 ${theme.spacing()}px`,
  },
  nameInputContainer: {
    margin: `${theme.spacing() * 4}px ${theme.spacing() * 2}px ${theme.spacing()}px`,
  },
  resetButtonContainer: {
    margin: `0 ${theme.spacing() * 2}px ${theme.spacing() * 2}px`,
  },
})

type InjectedProps = Props & RouteComponentProps<{}> & WithStyles<typeof styles> & WrappedComponentProps

class SettingsScreen extends React.Component<InjectedProps, State> {
  photoBlob?: Blob

  constructor(props: InjectedProps) {
    super(props)
    this.state = {
      isSomethingChanged: false,
      nickname: props.userInfo.user!.name!,
      photoUrl: props.userInfo.user!.photoUrl!,
      notificationSettings: { isLoading: true },
    }

    this.loadNotificationSettings()
  }

  private loadNotificationSettings = () => {
    this.props.getNotifSettings()
      .then((result: NotificationSettingsResponse) => {
        this.setState({ notificationSettings: { isLoading: false, result } })
      })
      .catch((err: Error) => {
        this.setState({ notificationSettings: { isLoading: false, errorCode: err.message } })
      })
  }

  private onPhotoSelect = (blob: Blob, url: string) => {
    this.photoBlob = blob
    this.setState({ photoUrl: url, isSomethingChanged: true })
  }

  private onNotificationToggle = (notificationId: string, enabled: boolean) => {
    // NOTE: Changing the array inside the state. Without calling setState() or forceUpdate()
    // React would not detect this change. Luckily, the setState() is called right after this
    // statement, see below.
    this.state.notificationSettings.result!.notification = this.state.notificationSettings.result!.notification
      .map((notification) =>
        notification.notificationId === notificationId
          ? NotificationType.fromObject({ ...notification, enabled })
          : notification
      )

    this.setState({ isSomethingChanged: true })
  }

  private onSubmit = () => {
    const notificationSettings = this.state.notificationSettings.result!.notification
      .filter(type => type.available)
      .reduce((result, type) => {
        result[type.notificationId!] = Boolean(type.enabled)
        return result
      }, {} as { [k: string]: boolean })

    this.props.changeUser(this.state.nickname, this.photoBlob, notificationSettings)
      .then(() => {
        this.props.onUserInfoChange(this.state.nickname, this.state.photoUrl)

        this.setState({ isSomethingChanged: false })
        this.photoBlob = undefined
      })
      .catch((err: EditProfileResponse) => {
        const { intl } = this.props
        let errorCode: string = ''

        switch (err.result) {
          case EditProfileResponse.Result.EPR_INVALID_NAME:
            errorCode = intl.formatMessage({ id: 'account_action_changename_validationerror_title' })
            break
          case EditProfileResponse.Result.EPR_INVALID_PHOTO:
            errorCode = intl.formatMessage({ id: 'account_action_changephoto_uploaderror_title' })
            break
          default:
            errorCode = intl.formatMessage({ id: 'common_errors_message_unexpected' })
        }

        this.setState({ errorCode })
      })
  }

  private onNicknameChange = (nickname: string) => {
    this.setState({ nickname, isSomethingChanged: true })
  }

  private renderContent = () => {
    const { userInfo, onLogoutClick, classes } = this.props
    const { notificationSettings, nickname, photoUrl, isSomethingChanged } = this.state

    if (!userInfo.user || !notificationSettings.result) {
      return null
    }

    return (
      <>
        <Typography variant="subtitle1" align="center" className={classes.userName}>{nickname}</Typography>
        <Grid container justify="center">
          <Grid item>
            <UserPhotoWithUploadAndCropper
              showUploadIcon={userInfo.user.photoUrl === photoUrl}
              photoUrl={photoUrl}
              onPhotoAccept={this.onPhotoSelect}
            />
          </Grid>
        </Grid>
        <div className={classes.nameInputContainer}>
          <TextInput
            value={nickname}
            onChange={this.onNicknameChange}
          />
        </div>
        <div className={classes.resetButtonContainer}>
          <Button fullWidth color="secondary" variant="contained" to="/resetpassword">
            <T id="onboarding_login_passwordreset_title" />
          </Button>
        </div>
        <NotificationSwitchers
          notificationSettings={notificationSettings.result}
          onNotificationToggle={this.onNotificationToggle}
        />
        <Snackbar
          open={!!this.state.errorCode}
          autoHideDuration={3000}
          onClose={() => { this.setState({ errorCode: undefined }) }}
          message={this.state.errorCode}
        />
        <div className={classes.logoutButtonContainer}>
          <Button fullWidth color="dangerous" variant="contained" onClick={onLogoutClick}>
            <T id="account_action_logout" />
          </Button>
        </div>
        <CenteredBottom fillHeight={54} className={classes.updateButtonContainer}>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            disabled={!isSomethingChanged || !nickname}
            onClick={this.onSubmit}
          >
            <T id="settings_action_update" />
          </Button>
        </CenteredBottom>
      </>
    )
  }

  render() {
    const { history } = this.props
    const { notificationSettings } = this.state

    return (
      <>
        <NavigationHeader
          title={<T id="settings_title" />}
          onBackClick={history.goBack}
        />
        <CenteredContent>
          <ActionContext
            isLoading={notificationSettings.isLoading}
            errorCode={notificationSettings.errorCode}
            component={this.renderContent}
            componentProps={{}}
          />
        </CenteredContent>
      </>
    )
  }
}

export default withStyles(styles)(injectIntl(SettingsScreen))
